import React, { useEffect, useState, useMemo, useCallback, memo, useRef, forwardRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import ConfirmationModal from './ConfirmationModal';
import SuccessNotification from './SuccessNotification';
import { FixedSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import '../pages/ViewDataPage/styles.css';
import styles from './ViewDataContent.module.css';
import { API_BASE_URL as API_URL } from '../config/apiBase';


const DenseDataRow = memo(({ index, style, data }) => {
  const { rows, columns, selectedIds, toggleSelectOne, gridTemplateColumns } = data;
  const record = rows[index];
  if (!record) return null;

  const isSelected = selectedIds.has(record.record_id);
  const rowClass = [
    styles.tableRow,
    index % 2 === 0 ? styles.rowEven : styles.rowOdd,
    isSelected ? styles.rowSelected : ''
  ]
    .filter(Boolean)
    .join(' ');

  const resolveValue = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    if (typeof value === 'object') {
      if (value.label) return String(value.label);
      if (value.ref_record_id) return String(value.label || value.ref_record_id);
      try {
        return JSON.stringify(value);
      } catch (error) {
        return String(value);
      }
    }
    return String(value);
  };

  return (
    <div
      style={{ ...style, gridTemplateColumns }}
      className={rowClass}
      role="row"
      aria-selected={isSelected}
    >
      {columns.map((column) => {
        if (column.id === '__select') {
          return (
            <div key={column.id} className={`${styles.cell} ${styles.checkboxCell}`} role="gridcell">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => toggleSelectOne(record.record_id)}
                aria-label={`Select row ${index + 1}`}
              />
            </div>
          );
        }

        const value = resolveValue(record.record_data?.[column.id]);
        return (
          <div key={column.id} className={styles.cell} role="gridcell" title={value}>
            {value}
          </div>
        );
      })}
    </div>
  );
});

const VirtualListOuter = forwardRef(({ style, ...rest }, ref) => (
  <div
    ref={ref}
    style={{ ...style, overflowX: 'hidden' }}
    {...rest}
  />
));
VirtualListOuter.displayName = 'VirtualListOuter';

const ViewDataContent = ({
  schemaId,
  isInline = false,
  onAddData,
  onBack,
  adminMode = false,
  entityId = null,
  includeDeleted = false,
  onToggleIncludeDeleted = null,
  refreshSignal = null
}) => {
  const navigate = useNavigate();
  const [schema, setSchema] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [chartType, setChartType] = useState('bar');
  const [xField, setXField] = useState('');
  const [yField, setYField] = useState('');
  const [showChart, setShowChart] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [totalRecords, setTotalRecords] = useState(0);

  useEffect(() => {
    if (schemaId) {
      fetchSchema();
      fetchData();
    }
  }, [schemaId]);

  // Skeleton-only refresh for filter/context changes (adminMode/includeDeleted/entityId)
  useEffect(() => {
    const refreshDataSkeleton = async () => {
      if (!schemaId) return;
      setRefreshing(true);
      try {
        await fetchData();
      } finally {
        setRefreshing(false);
      }
    };
    refreshDataSkeleton();
  }, [includeDeleted, adminMode, entityId, schemaId, page]);

  useEffect(() => {
    if (!schemaId || refreshSignal === null) return;
    const runRefresh = async () => {
      setRefreshing(true);
      try {
        await fetchData();
      } finally {
        setRefreshing(false);
      }
    };
    runRefresh();
  }, [refreshSignal]);

  const fetchSchema = async () => {
    try {
      const token = localStorage.getItem('token');
      // Include entity_id query parameter if in admin mode
      const url = adminMode && entityId
        ? `${API_URL}/schemas/${schemaId}?entity_id=${entityId}`
        : `${API_URL}/schemas/${schemaId}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch schema');
      }

      const result = await response.json();
      setSchema(result.data);
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      // Use admin endpoint if adminMode is enabled
      const extra = `${includeDeleted ? '&include_deleted=true' : ''}&limit=${pageSize}&page=${page}`;
      const dataUrl = adminMode && entityId
        ? `${API_URL}/admin/entities/${entityId}/data?schema_id=${schemaId}${extra}`
        : `${API_URL}/visuals/data?schema_id=${schemaId}${extra}`;

      const response = await fetch(dataUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch data');
      }

      const result = await response.json();
      setData(result.data?.records || result.data || []);
      if (result.data?.totalRecords != null) setTotalRecords(result.data.totalRecords);
      if (result.data?.page != null) setPage(result.data.page);
      if (result.data?.pageSize != null) setPageSize(result.data.pageSize);
      setSelectedIds(new Set());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const selectAllEnabled = data.length > 0 && selectedIds.size === data.length;

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      if (prev.size === data.length) {
        return new Set();
      }
      return new Set((data || []).map((record) => record.record_id));
    });
  }, [data]);

  const toggleSelectOne = useCallback((recordId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(recordId)) {
        next.delete(recordId);
      } else {
        next.add(recordId);
      }
      return next;
    });
  }, []);

  const handleDeleteClick = () => {
    if (selectedIds.size === 0) return;
    setShowConfirmModal(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    setShowConfirmModal(false);
    setRefreshing(true);

    try {
      const token = localStorage.getItem('token');
      const endpoint = adminMode && entityId
        ? `${API_URL}/admin/entities/${entityId}/data/delete`
        : `${API_URL}/visuals/data/delete`;
      const body = {
        schema_id: schemaId,
        record_ids: Array.from(selectedIds)
      };
      const resp = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      if (!resp.ok) {
        const t = await resp.text();
        throw new Error(t || 'Failed to delete records');
      }

      const result = await resp.json();
      const deletedCount = result.deletedCount ?? result.deleted ?? selectedIds.size;
      const failedIds = result.failedIds || [];

      // Show success message
      if (failedIds.length > 0) {
        setSuccessMessage(`Deleted ${deletedCount}, failed ${failedIds.length}: ${failedIds.slice(0, 5).join(', ')}${failedIds.length > 5 ? '...' : ''}`);
      } else {
        setSuccessMessage(`Successfully deleted ${deletedCount} ${deletedCount === 1 ? 'record' : 'records'}`);
      }
      setShowSuccess(true);

      // Refresh data after a short delay to show skeleton
      setTimeout(async () => {
        await fetchData();
        setRefreshing(false);
      }, 800);

    } catch (err) {
      setError(err.message || 'Failed to delete');
      setRefreshing(false);
    } finally {
      setDeleting(false);
    }
  };

  const dataColumns = useMemo(() => {
    if (data.length === 0) return [];
    const firstRecord = data[0];
    if (firstRecord?.record_data) {
      return Object.keys(firstRecord.record_data);
    }
    return [];
  }, [data]);

  const numericFields = useMemo(() => {
    return dataColumns.filter((column) =>
      data.every((record) => {
        const value = record.record_data?.[column];
        return value === null || value === undefined || !Number.isNaN(Number(value));
      })
    );
  }, [dataColumns, data]);

  const categoricalFields = useMemo(
    () => dataColumns.filter((column) => !numericFields.includes(column)),
    [dataColumns, numericFields]
  );

  const tableColumnsStructure = useMemo(() => {
    const computed = dataColumns.map((column) => {
      const approxLength = column.length;
      const width = Math.min(260, Math.max(120, approxLength * 12));
      return { id: column, label: column, width };
    });
    return [{ id: '__select', label: '', width: 48 }, ...computed];
  }, [dataColumns]);

  const gridTemplateColumns = useMemo(
    () => tableColumnsStructure.map((column) => `${column.width}px`).join(' '),
    [tableColumnsStructure]
  );

  const tableWidth = useMemo(
    () => tableColumnsStructure.reduce((sum, column) => sum + column.width, 0),
    [tableColumnsStructure]
  );

  const tableContainerRef = useRef(null);
  const [viewportWidth, setViewportWidth] = useState(0);

  useEffect(() => {
    if (!tableContainerRef.current || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry?.contentRect?.width != null) {
        setViewportWidth(entry.contentRect.width);
      }
    });
    observer.observe(tableContainerRef.current);
    setViewportWidth(tableContainerRef.current.clientWidth);
    return () => observer.disconnect();
  }, []);

  const effectiveTableWidth = useMemo(
    () => Math.max(tableWidth, viewportWidth || 0),
    [tableWidth, viewportWidth]
  );

  const listData = useMemo(
    () => ({
      rows: data,
      columns: tableColumnsStructure,
      selectedIds,
      toggleSelectOne,
      gridTemplateColumns
    }),
    [data, tableColumnsStructure, selectedIds, toggleSelectOne, gridTemplateColumns]
  );

  const itemKey = useCallback(
    (index, itemData) => itemData.rows[index]?.record_id ?? `row-${index}`,
    []
  );

  const prepareChartData = () => {
    if (!xField || !yField) return [];

    return data.map(record => ({
      [xField]: record.record_data[xField],
      [yField]: parseFloat(record.record_data[yField]) || 0
    }));
  };

  const renderChart = () => {
    const chartData = prepareChartData();

    if (chartData.length === 0) return null;

    const commonProps = {
      data: chartData,
      margin: { top: 20, right: 30, left: 20, bottom: 5 }
    };

    switch (chartType) {
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey={xField} />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey={yField} fill="#3b82f6" />
            </BarChart>
          </ResponsiveContainer>
        );

      case 'line':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey={xField} />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey={yField} stroke="#3b82f6" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        );

      case 'pie':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey={yField}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={`hsl(${index * 60}, 70%, 50%)`} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className={isInline ? "view-data-inline-loading" : "view-data-loading"}>
        <div className="loading-spinner"></div>
        <p>Loading data...</p>
      </div>
    );
  }

  if (!schema) {
    return (
      <div className={isInline ? "view-data-inline-error" : "view-data-error"}>
        <h3>Schema not found</h3>
        <p>The requested table schema could not be found.</p>
      </div>
    );
  }

  const columns = dataColumns;

  const containerClass = isInline ? "view-data-inline-container" : "view-data-container";
  const headerClass = isInline ? "view-data-inline-header" : "view-data-header";
  const sectionClassName = `view-data-section${isInline ? ' view-data-inline-section' : ''}`;
  const rootStyle = isInline ? { maxWidth: '100%', overflow: 'hidden' } : undefined;

  return (
    <div className={`${containerClass} ${isInline ? 'view-data-inline-equal' : ''}`} style={rootStyle}>
      {!isInline && (
        <div className={headerClass}>
          {/* <div>
            <h1>{schema.table_name}</h1>
            <p>{isInline ? "View and analyze your data" : "View and analyze your data"}</p>
          </div> */}
          <div className="view-data-actions">
            <button
              onClick={() => {
                if (onAddData) {
                  onAddData(schemaId);
                } else if (adminMode && entityId) {
                  navigate(`/admin/entities/${entityId}/add-data/${schemaId}`);
                } else {
                  navigate(`/visuals/add-data/${schemaId}`);
                }
              }}
              className="add-data-btn"
            >
              + Add Data
            </button>
            <button
              onClick={() => {
                if (onBack) {
                  onBack();
                } else if (adminMode && entityId) {
                  navigate(`/admin/entities/${entityId}/tables`);
                } else {
                  navigate('/visuals/tables');
                }
              }}
              className="view-data-back-btn"
            >
              Back to Tables
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="view-data-error">
          {error}
        </div>
      )}

      <div className="view-data-content">
        {/* Chart Section */}

        {/* Table Section */}
        <div className={sectionClassName}>
          <div className={styles.tableCard}>
            <div className={styles.tableHeader}>
              <div>
                <p className={styles.kicker}>Dataset</p>
                <h2 className={styles.title}>{schema.table_name}</h2>
                <p className={styles.caption}>
                  {totalRecords > 0
                    ? `Showing ${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, totalRecords)} of ${totalRecords}`
                    : `${data.length} records`}
                </p>
              </div>
              <div className={styles.tableStats}>
                <span className={styles.statBadge}>{data.length} records</span>
                <span className={styles.statBadge}>{columns.length} columns</span>
                {/* {numericFields.length > 0 && (
                  <span className={styles.statBadge}>{numericFields.length} numeric</span>
                )} */}
              </div>
            </div>

            <div className={styles.tableToolbar}>
              <div className={styles.tableToolbarLeft}>
                <span className={styles.toolbarHint}>
                  {totalRecords > 0 ? `Page ${page} · ${pageSize} per page` : 'All rows loaded'}
                </span>
                {adminMode && typeof includeDeleted === 'boolean' && typeof onToggleIncludeDeleted === 'function' ? (
                  <label className={styles.toggleDeleted}>
                    <input
                      type="checkbox"
                      checked={!!includeDeleted}
                      onChange={onToggleIncludeDeleted}
                    />
                    Show deleted
                  </label>
                ) : null}
              </div>
              <div className={styles.tableToolbarRight}>
                <button
                  className="danger-btn"
                  onClick={handleDeleteClick}
                  disabled={selectedIds.size === 0 || deleting || refreshing}
                  title={selectedIds.size === 0 ? 'Select rows to delete' : 'Delete selected rows'}
                >
                  {deleting ? 'Deleting…' : `Delete Selected (${selectedIds.size})`}
                </button>
                <div className={styles.paginationGroup}>
                  <button
                    className={styles.paginationButton}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1 || refreshing || deleting}
                  >
                    Prev
                  </button>
                  <button
                    className={styles.paginationButton}
                    onClick={() => setPage((p) => (p * pageSize < totalRecords ? p + 1 : p))}
                    disabled={page * pageSize >= totalRecords || refreshing || deleting}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>

            {data.length > 0 ? (
              <div className={styles.tableSurface}>
                <div className={styles.horizontalScroll} ref={tableContainerRef}>
                  <div
                    className={styles.tableBodySurface}
                    style={{ width: effectiveTableWidth }}
                  >
                    <div
                      className={styles.tableHeaderRow}
                      role="row"
                      style={{ gridTemplateColumns }}
                    >
                      {tableColumnsStructure.map((column) => (
                        <div
                          key={column.id}
                          className={styles.columnHeader}
                          style={{ width: column.width }}
                          role="columnheader"
                        >
                          {column.id === '__select' ? (
                            <input
                              type="checkbox"
                              checked={selectAllEnabled}
                              onChange={toggleSelectAll}
                              aria-label="Select all rows"
                            />
                          ) : (
                            column.label
                          )}
                        </div>
                      ))}
                    </div>
                    <div className={styles.virtualWrapper}>
                      <AutoSizer disableWidth>
                        {({ height }) => (
                          <List
                            height={height}
                            width={effectiveTableWidth}
                            itemCount={data.length}
                            itemSize={36}
                            itemData={listData}
                            itemKey={itemKey}
                            className={styles.virtualList}
                            overscanCount={18}
                            outerElementType={VirtualListOuter}
                          >
                            {DenseDataRow}
                          </List>
                        )}
                      </AutoSizer>
                    </div>
                  </div>
                </div>
                {refreshing && (
                  <div className={styles.refreshOverlay}>
                    {Array.from({ length: 6 }).map((_, idx) => (
                      <div
                        key={`refresh-${idx}`}
                        className={`${styles.tableRow} ${styles.skeletonRow}`}
                        style={{ gridTemplateColumns }}
                      >
                        {tableColumnsStructure.map((column) => (
                          <div key={`${column.id}-${idx}`} className={styles.skeletonCell}>
                            <span className={styles.shimmer} />
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className={styles.emptyState}>
                <p className={styles.emptyTitle}>No records yet</p>
                <p className={styles.emptyText}>
                  Add or upload data to populate this table. Entries will appear instantly.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Records"
        message={`Are you sure you want to delete ${selectedIds.size} selected ${selectedIds.size === 1 ? 'record' : 'records'}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        icon="🗑️"
        loading={deleting}
      />

      {/* Success Notification */}
      <SuccessNotification
        isVisible={showSuccess}
        message={successMessage}
        onClose={() => setShowSuccess(false)}
        duration={4000}
      />
    </div>
  );
};

export default ViewDataContent;

